import type { RichAccessControl } from "@eu.mdotm.api/smart-workspaces";
import { runWithErrorReporting } from "$root/api/error-reporting/report";
import type { EntityManagementActions } from "$root/components/spawnable/entity-management/actions";
import { spawnDeleteDialog } from "$root/components/spawnable/entity-management/delete-dialog";
import { spawnDuplicateDialog } from "$root/components/spawnable/entity-management/duplicate-dialog";
import { spawnRenameDialog } from "$root/components/spawnable/entity-management/rename-dialog";
import { platformToast } from "$root/notification-system/toast";
import { ToastableError } from "$root/utils";
import { t } from "i18next";
import { useMemo } from "react";
import { aclByArea } from "../acl/checkers/all";
import { useUserValue } from "../user";
import { marketViewRepository } from "./repository";

export function useMarketViewEntityManagementActions(
	marketView:
		| {
				name?: string;
				uuid?: string;
				richAcl?: { acl?: RichAccessControl[] };
		  }
		| null
		| undefined,
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	const user = useUserValue();
	return useMemo(
		() => (!marketView ? {} : marketViewEntityManagementActions(user, marketView, callbacks)),
		[callbacks, marketView, user],
	);
}

export function marketViewEntityManagementActions(
	user: { id: string },
	marketView: {
		name?: string;
		uuid?: string;
		richAcl?: { acl?: RichAccessControl[] };
	},
	callbacks?: {
		onRename?(uuid: string): void;
		onDelete?(uuid: string): void;
		onDuplicate?(uuid: string): void;
	},
): Partial<Omit<EntityManagementActions, "createAsync">> {
	return {
		deleteAsync: !aclByArea.marketView.canDelete(user.id, marketView?.richAcl?.acl ?? [])
			? undefined
			: () =>
					spawnDeleteDialog({
						entityName: marketView?.name ?? "",
						entityType: "market view",
						yesButton: "Proceed",
						noButton: "Cancel",
						async onDeleteAsync() {
							await runWithErrorReporting(
								async () => {
									try {
										await marketViewRepository.delete(marketView.uuid ?? "");
										platformToast({
											children: t("MODAL.MARKET_DELETED_TITLE_SUCCESS", { name: marketView?.name ?? "" }),
											severity: "success",
											icon: "Portfolio",
										});
										callbacks?.onDelete?.(marketView.uuid ?? "");
									} catch (err) {
										throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
									}
								},
								{
									area: "market-views",
									attemptedOperation: {
										message: `delete market view "${marketView.uuid}"`,
										payload: JSON.stringify(marketView),
									},
								},
							);
						},
					}),
		duplicateAsync: () =>
			spawnDuplicateDialog({
				entityType: "marketView",
				originalName: marketView?.name ?? "",
				checkIfNameIsAvailable: marketViewRepository.isMarketViewNameAvailable,
				onSubmitAsync(name) {
					return runWithErrorReporting(
						async () => {
							try {
								const { uuid } = await marketViewRepository.duplicate(marketView.uuid ?? "", name);
								platformToast({
									children: t("MODAL.MARKET_DUPLICATE_TITLE_SUCCESS", { name }),
									severity: "success",
									icon: "Portfolio",
								});
								callbacks?.onDuplicate?.(uuid!);
								return uuid!;
							} catch (err) {
								throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
							}
						},
						{
							area: "market-views",
							attemptedOperation: {
								message: `duplicate market view "${marketView.uuid}"`,
								payload: JSON.stringify(marketView),
							},
						},
					);
				},
			}),
		renameAsync: !aclByArea.marketView.canRename(user.id, marketView?.richAcl?.acl ?? [])
			? undefined
			: () =>
					spawnRenameDialog({
						entityType: "marketView",
						currentName: marketView?.name ?? "",
						checkIfNameIsAvailable: marketViewRepository.isMarketViewNameAvailable,
						onSubmitAsync(name) {
							return runWithErrorReporting(
								async () => {
									try {
										await marketViewRepository.rename(marketView.uuid ?? "", name);
										platformToast({
											children: t("MODAL.MARKET_RENAME_TITLE_SUCCESS", { name }),
											severity: "success",
											icon: "Portfolio",
										});
										callbacks?.onRename?.(name);
										return name;
									} catch (err) {
										throw new ToastableError(t("SOMETHING_WENT_WRONG"), { cause: err, icon: "Portfolio" });
									}
								},
								{
									area: "market-views",
									attemptedOperation: {
										message: `rename market view "${marketView.uuid}"`,
										payload: JSON.stringify(marketView),
									},
								},
							);
						},
					}),
	};
}
